import { useRef } from 'react';

/**
 * Custom hook for managing session and navigation state related to polygons.
 *
 * Manages session state during polygon operations, including:
 * - Saving the original location before drawing a polygon
 * - Maintaining map bounds centered on the polygon
 * - Tracking polygon-related actions
 * - Controlling item location injection
 *
 * @returns {Object} Object with the following functions:
 * @returns {Function} returns.saveLocationBeforePolygon - Saves current location before drawing
 * @returns {Function} returns.saveCenteredPolygonBounds - Saves polygon centered bounds
 * @returns {Function} returns.getCenteredPolygonBounds - Gets saved centered bounds
 * @returns {Function} returns.getSavedLocation - Gets saved location
 * @returns {Function} returns.getSavedMapBounds - Gets saved map bounds
 * @returns {Function} returns.clearSavedState - Clears all saved state
 * @returns {Function} returns.markPolygonAction - Marks that a polygon action was performed
 * @returns {Function} returns.wasLastActionPolygon - Checks if last action was polygon-related
 * @returns {Function} returns.markItemLocationInjected - Marks that item location was injected
 * @returns {Function} returns.wasItemLocationInjected - Checks if item location was injected
 *
 * @example
 * ```jsx
 * const {
 *   saveLocationBeforePolygon,
 *   getSavedLocation,
 *   clearSavedState,
 *   wasLastActionPolygon
 * } = usePolygonSession();
 *
 * // Save location before drawing
 * saveLocationBeforePolygon({
 *   pathname: '/search/category',
 *   search: '?query=test',
 *   mapBounds: mapInstance.getBounds()
 * });
 * ```
 */
const usePolygonSession = () => {
  const originalLocationRef = useRef(null);
  const originalMapBoundsRef = useRef(null);
  const centeredPolygonBoundsRef = useRef(null);
  const lastActionWasPolygon = useRef(false);
  const itemLocationInjected = useRef(false);

  /**
   * Saves the current location and map bounds before starting polygon drawing.
   *
   * @param {Object} params - Location parameters
   * @param {string} params.pathname - Current page path
   * @param {string} params.search - Current search parameters
   * @param {google.maps.LatLngBounds} [params.mapBounds=null] - Current map bounds
   */
  const saveLocationBeforePolygon = ({ pathname, search, mapBounds = null }) => {
    originalLocationRef.current = { pathname, search };
    originalMapBoundsRef.current = mapBounds;
    lastActionWasPolygon.current = true;
    itemLocationInjected.current = false;
  };

  /**
   * Saves the map bounds centered on the polygon.
   *
   * @param {google.maps.LatLngBounds} mapBounds - Map bounds centered on the polygon
   */
  const saveCenteredPolygonBounds = (mapBounds) => {
    centeredPolygonBoundsRef.current = mapBounds;
  };

  /**
   * Gets the map bounds centered on the polygon.
   *
   * @returns {google.maps.LatLngBounds|null} Saved centered bounds or null
   */
  const getCenteredPolygonBounds = () => centeredPolygonBoundsRef.current;

  /**
   * Gets the saved location before the polygon.
   *
   * @returns {Object|null} Object with pathname and search or null
   */
  const getSavedLocation = () => originalLocationRef.current;

  /**
   * Gets the saved map bounds before the polygon.
   *
   * @returns {google.maps.LatLngBounds|null} Saved map bounds or null
   */
  const getSavedMapBounds = () => originalMapBoundsRef.current;

  /**
   * Clears all saved session state.
   */
  const clearSavedState = () => {
    originalLocationRef.current = null;
    originalMapBoundsRef.current = null;
    centeredPolygonBoundsRef.current = null;
    lastActionWasPolygon.current = false;
    itemLocationInjected.current = false;
  };

  /**
   * Marks that a polygon-related action was performed.
   */
  const markPolygonAction = () => {
    lastActionWasPolygon.current = true;
  };

  /**
   * Checks if the last action was polygon-related and resets the flag.
   *
   * @returns {boolean} true if the last action was polygon-related, false otherwise
   */
  const wasLastActionPolygon = () => {
    const wasPolygon = lastActionWasPolygon.current;

    lastActionWasPolygon.current = false;

    return wasPolygon;
  };

  /**
   * Marks that an item location was injected into the URL.
   */
  const markItemLocationInjected = () => {
    itemLocationInjected.current = true;
  };

  /**
   * Checks if an item location was injected.
   *
   * @returns {boolean} true if location was injected, false otherwise
   */
  const wasItemLocationInjected = () => itemLocationInjected.current;

  return {
    saveLocationBeforePolygon,
    saveCenteredPolygonBounds,
    getCenteredPolygonBounds,
    getSavedLocation,
    getSavedMapBounds,
    clearSavedState,
    markPolygonAction,
    wasLastActionPolygon,
    markItemLocationInjected,
    wasItemLocationInjected,
  };
};

export default usePolygonSession;
